import React from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useGetFeatureFlagQuery, useUpdateFeatureFlagMutation } from './api/featureFlagsApi';
import { FEATURE_FLAG_TARGETS } from './targets';
import { FeatureFlagTargetChip } from './FeatureFlagTargetChip';
import { useAdminConfirm } from '../shared/AdminConfirmProvider';
import { useFeatureFlagRules } from './useFeatureFlagRules';
import { getTargetName } from './getTargetName';

/**
 * A feature flag's users, broker groups or ledgers (`field`) as chips; a chip's × removes it
 * after the admin confirms. Chips the rules don't allow removing (see useFeatureFlagRules)
 * have no ×. Reads the current flag by id, so it updates as soon as something is added or
 * removed. limit: show only the first `limit` chips (the table cell when collapsed). Used by
 * the table cell.
 */
export const FeatureFlagTargetChips = ({ flagId, field, limit }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { data: flag } = useGetFeatureFlagQuery(flagId);
	const { canRemove } = useFeatureFlagRules(flag, field);
	const [updateFeatureFlag, { isLoading }] = useUpdateFeatureFlagMutation();
	const { singular } = FEATURE_FLAG_TARGETS[field];

	const handleRemove = async (event, id) => {
		// Removing a chip in the table cell shouldn't also open the add dialog
		event.stopPropagation();
		const name = await getTargetName(dispatch, field, id);
		const confirmed = await confirm({
			title: `Remove ${name} from ${flag.feature}?`,
			confirmText: 'Remove',
		});
		if (!confirmed) return;

		const result = await updateFeatureFlag({ id: flagId, [field]: { remove: [id] } });
		if (result.error) {
			dispatch(
				showToast({
					message: `Couldn't remove that ${singular} from ${flag.feature}. Please try again.`,
					error: true,
				}),
			);
		}
	};

	return (flag?.[field] ?? []).slice(0, limit).map((id) => (
		<FeatureFlagTargetChip
			key={id}
			field={field}
			id={id}
			{...(canRemove(id) && { onDelete: (event) => handleRemove(event, id) })}
			disabled={isLoading}
		/>
	));
};
